'use client'
import { useState, useEffect } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { clients } from '@/lib/data/clients'
import { heroStats } from '@/lib/data/stats'
import {
  SlideFromLeft,
  SlideFromRight,
  FadeInUp,
  Stagger,
  StaggerItem,
} from '@/components/ui/FadeInUp'
import { EASE } from '@/lib/constants/animations'
import CountUp from '@/components/ui/CountUp'
import AssetPicture from '@/components/ui/AssetPicture'
import { assetUrl } from '@/lib/assetUrl'
import { toPersianNums } from '@/lib/persian'

const LEFT_IMAGE = 'photos/brand-art/network-trust.jpg'
const DARK_CARD_IMAGE = 'photos/brand-art/glow-dark.jpg'

export default function SocialProof() {
  const { t, lang } = useLanguage()
  const p = t.proof

  const [statIndex, setStatIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setStatIndex((i) => (i + 1) % heroStats.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  return (
    <section id="about" className="py-14 md:py-20 border-b border-ink/5 bg-[#f6f8fc]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          {/* ── Left: large image ── */}
          <SlideFromLeft>
            <div className="relative rounded-3xl overflow-hidden min-h-[260px] sm:min-h-[360px] md:min-h-[460px] h-full">
              <AssetPicture
                src={LEFT_IMAGE}
                alt="Trend Agency"
                width={1344}
                height={1800}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: 'linear-gradient(160deg, rgba(3,53,183,0.12) 0%, transparent 50%)',
                }}
              />
            </div>
          </SlideFromLeft>

          {/* ── Right: content ── */}
          <SlideFromRight delay={0.08} className="flex flex-col gap-5">
            {/* Section label */}
            <FadeInUp delay={0.05}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">
                {p.heading}
              </p>
            </FadeInUp>

            {/* Mixed bold / muted heading */}
            <FadeInUp delay={0.1}>
              <h2 className="font-display font-black text-2xl md:text-[1.75rem] leading-snug">
                <span className="text-ink">{p.aboutBold} </span>
                <span className="text-ink/35 font-normal">{p.aboutMuted}</span>
              </h2>
            </FadeInUp>

            {/* Single stat */}
            <FadeInUp delay={0.14}>
              <div className="flex items-center gap-3 py-4 border-t border-ink/8">
                <div className="w-8 h-8 rounded-full border border-ink/10 flex items-center justify-center text-muted flex-shrink-0">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.8}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-bold text-ink leading-tight">
                    {t.proof.campaignsTitle}
                  </p>
                  <p className="text-[11px] text-muted leading-tight">{t.proof.campaignsSub}</p>
                </div>
              </div>
            </FadeInUp>

            {/* ── Two cards ── */}
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1" staggerDelay={0.08}>
              {/* Card 1: white — clients + logos + CTA */}
              <StaggerItem>
                <div className="bg-white rounded-2xl border border-ink/5 shadow-sm p-5 flex flex-col gap-4 h-full">
                  <div>
                    <p className="font-display font-black text-[2.75rem] leading-none text-ink">
                      <CountUp target={600} suffix="+" />
                    </p>
                    <p className="text-[11px] text-muted mt-1 uppercase tracking-wide">
                      {t.proof.enterpriseLabel}
                    </p>
                  </div>
                  {/* Scrolling logo strip inside card. dir="ltr" pins the
                      flex flow LTR regardless of page language — without
                      it, the RTL flex layout parks the track off-screen
                      and the strip stays empty for several seconds before
                      the animation drags it back into view. */}
                  <div
                    dir="ltr"
                    className="overflow-hidden flex-1 flex items-center"
                    style={{
                      maskImage:
                        'linear-gradient(90deg, transparent 0%, black 20%, black 80%, transparent 100%)',
                      WebkitMaskImage:
                        'linear-gradient(90deg, transparent 0%, black 20%, black 80%, transparent 100%)',
                    }}
                  >
                    <div
                      className="flex items-center marquee-track-fast"
                      style={{ width: 'max-content' }}
                    >
                      {[...clients, ...clients].map((client, i) => (
                        // pr-10 (not gap on the parent) so the spacing rides
                        // with each item — keeps the -50% wrap seamless. With
                        // a parent gap, the slot between half1's last and
                        // half2's first is the same as elsewhere, but the
                        // math of translateX(-50%) only accounts for half
                        // the gaps, leaving a visible jump at the boundary.
                        <div
                          key={`card-${client.slug}-${i}`}
                          className="flex-shrink-0 h-5 pr-10 flex items-center justify-center opacity-40"
                        >
                          <Image
                            src={assetUrl(client.logo)}
                            alt={lang === 'fa' ? client.fa : client.en}
                            width={60}
                            height={20}
                            className="object-contain h-full"
                            // grayscale (not brightness(0)) so brand tiles
                            // like Snapp still read as their actual mark
                            // instead of a solid black square.
                            style={{ filter: 'grayscale(1)', width: 'auto' }}
                            unoptimized
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <a
                    href="#contact"
                    className="w-full py-2.5 rounded-xl font-semibold text-white text-sm text-center block transition-all duration-200 hover:opacity-80"
                    style={{ background: '#0b1629' }}
                  >
                    {t.hero.cta1}
                  </a>
                </div>
              </StaggerItem>

              {/* Card 2: dark image — auto-sliding stat carousel */}
              <StaggerItem>
                <div className="relative rounded-2xl overflow-hidden h-full min-h-[200px] sm:min-h-[220px]">
                  {/* Background image */}
                  <AssetPicture
                    src={DARK_CARD_IMAGE}
                    alt=""
                    aria-hidden="true"
                    width={1400}
                    height={1400}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />

                  {/* Dark gradient */}
                  <div
                    className="absolute inset-0"
                    style={{
                      background:
                        'linear-gradient(160deg, rgba(0,0,0,0.15) 0%, rgba(3,15,60,0.92) 100%)',
                    }}
                  />

                  <div className="absolute inset-0 p-5 flex flex-col justify-between">
                    {/* Top label + counter */}
                    <div className="flex items-start justify-between">
                      <span className="text-white/45 text-[10px] font-semibold uppercase tracking-widest">
                        {t.proof.factLabel}
                      </span>
                      <span className="text-white/25 text-[10px] tabular-nums">
                        {lang === 'fa'
                          ? `${toPersianNums(String(statIndex + 1).padStart(2, '0'))}/${toPersianNums(heroStats.length)}`
                          : `${String(statIndex + 1).padStart(2, '0')}/${heroStats.length}`}
                      </span>
                    </div>

                    {/* Sliding stat — live region so screen readers announce the change */}
                    <div aria-live="polite" aria-atomic="true">
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={statIndex}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -12 }}
                          transition={{ duration: 0.45, ease: EASE }}
                        >
                          <p className="font-display font-black text-[2rem] leading-none text-white">
                            <CountUp
                              key={statIndex}
                              target={heroStats[statIndex].value}
                              suffix={heroStats[statIndex].suffix}
                            />
                          </p>
                          <p className="text-white/50 text-xs mt-1.5 leading-snug">
                            {heroStats[statIndex].label[lang]}
                          </p>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Progress dots */}
                    <div className="flex gap-1.5">
                      {heroStats.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setStatIndex(i)}
                          className={`h-0.5 rounded-full transition-all duration-300 ${
                            i === statIndex
                              ? 'w-6 bg-white/70 hover:bg-white/90'
                              : 'w-2 bg-white/20 hover:bg-white/45 hover:w-3'
                          }`}
                          aria-label={`Stat ${i + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </StaggerItem>
            </Stagger>
          </SlideFromRight>
        </div>
      </div>
    </section>
  )
}
